import { memo, useState, useCallback, useEffect } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { useTenantUsage } from "@/hooks/useTenantUsage";
import { listIsles, createIsle, updateIsleStatus, isIsleError } from "@/infrastructure/isle/isleService";
import { isPlanLimitError } from "@/infrastructure/errors/planLimitError";
import { QRModal } from "../../components/QRModal";
import { listLocationsByIsle } from "@/infrastructure/location/locationService";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import type { Isle, IsleErrorCode, TypeUnit } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { DropdownSelector, Tooltip } from "@/design-system";
import { LocationDrawer } from "./LocationDrawer";
import { PlanLimitModal } from "../../components/PlanLimitModal";

export const SectionGrid = memo(function SectionGrid({ warehouseId, onIsleChange, onNavigateUsage }: { warehouseId: string; onIsleChange?: (isleId: string | null) => void; onNavigateUsage?: () => void }) {
  const { t } = useI18n();
  const { canCreateWarehouse, canEditWarehouse } = useRole();
  const { isAtLimit, isFeatureEnabled, refresh: refreshUsage } = useTenantUsage();
  const sectionAtLimit = isAtLimit("sectionsByWarehouse");
  const [isles, setIsles] = useState<Isle[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIsleId, setActiveIsleId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [planLimitMessage, setPlanLimitMessage] = useState<string | null>(null);
  const [qrItem, setQrItem] = useState<{ code: string; label: string } | null>(null);

  const fetchIsles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listIsles(warehouseId);
      setIsles(data);
      if (data.length > 0 && !activeIsleId) setActiveIsleId(data[0]!.id);
    } catch {}
    setLoading(false);
  }, [warehouseId]);

  useEffect(() => { setActiveIsleId(null); fetchIsles(); }, [fetchIsles]);

  useEffect(() => { onIsleChange?.(activeIsleId); }, [activeIsleId, onIsleChange]);

  const showToast = useCallback((msg: string, type: "success" | "error") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const getError = useCallback((code: IsleErrorCode) => {
    const map: Record<IsleErrorCode, string> = {
      DUPLICATE_NAME: t.isle.errorDuplicateName, VALIDATION: t.isle.errorValidation,
      NOT_FOUND: t.isle.errorForbidden, FORBIDDEN: t.isle.errorForbidden,
      UNAUTHORIZED: t.isle.errorForbidden, UNKNOWN: t.isle.errorForbidden,
    };
    return map[code];
  }, [t.isle]);

  const handleCreate = useCallback(async (input: Parameters<typeof createIsle>[0]) => {
    try {
      await createIsle(input);
      showToast(t.isle.successCreated, "success");
      setShowForm(false);
      fetchIsles();
      refreshUsage();
    } catch (err) {
      if (isPlanLimitError(err)) { setPlanLimitMessage(err.backendMessage); return; }
      if (isIsleError(err)) showToast(getError(err.message as IsleErrorCode), "error");
    }
  }, [fetchIsles, showToast, t.isle.successCreated, getError, refreshUsage]);

  const handleToggleStatus = useCallback(async (isle: Isle) => {
    const newStatus = isle.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateIsleStatus({ id: isle.id, status: newStatus });
      showToast(t.isle.successStatusChanged, "success");
      fetchIsles();
      refreshUsage();
    } catch (err) {
      if (isIsleError(err)) showToast(getError(err.message as IsleErrorCode), "error");
    }
  }, [fetchIsles, showToast, t.isle.successStatusChanged, getError, refreshUsage]);

  const activeIsle = isles.find(i => i.id === activeIsleId) ?? null;

  return (
    <div style={{ background: "#f8f8f8", borderRadius: "14px", border: "1px solid #efefef", padding: "20px", display: "flex", flexDirection: "column", gap: "18px", flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {t.isle.sectionOverview} ({isles.length})
        </h2>
        {canCreateWarehouse && (
          <Tooltip content={t.planLimit.tooltipDisabled} disabled={!sectionAtLimit}>
            <button
              onClick={() => setShowForm(true)}
              disabled={sectionAtLimit}
              style={{ fontFamily: typography.fontFamily, fontSize: "12px", fontWeight: typography.weight.semibold, color: sectionAtLimit ? "#bbb" : "#555", background: "transparent", border: `1px solid ${sectionAtLimit ? "#eee" : "#e0e0e0"}`, borderRadius: "8px", padding: "7px 14px", cursor: sectionAtLimit ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "5px", transition: `all ${transitions.fast}`, opacity: sectionAtLimit ? 0.6 : 1 }}
              onMouseEnter={e => { if (!sectionAtLimit) { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = "#111"; el.style.color = "#111"; } }}
              onMouseLeave={e => { if (!sectionAtLimit) { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = "#e0e0e0"; el.style.color = "#555"; } }}
            >
              + {t.isle.addSection}
            </button>
          </Tooltip>
        )}
      </div>

      {loading && <Spinner />}

      {!loading && isles.length === 0 && (
        <div style={{ padding: "48px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#444", margin: 0 }}>{t.isle.emptyTitle}</p>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb", margin: 0 }}>{t.isle.emptySubtitle}</p>
        </div>
      )}

      {!loading && isles.length > 0 && (
        <>
          <DropdownSelector
            items={isles.map(i => ({ id: i.id, name: i.name }))}
            activeId={activeIsleId}
            onSelect={setActiveIsleId}
          />
          {activeIsle && <IsleDetail isle={activeIsle} warehouseId={warehouseId} qrEnabled={isFeatureEnabled("qrBarcode")} onToggleStatus={() => handleToggleStatus(activeIsle)} onShowQR={(code, label) => setQrItem({ code, label })} />}
        </>
      )}

      {showForm && <CreateIsleModal warehouseId={warehouseId} onSave={handleCreate} onClose={() => setShowForm(false)} />}
      {toast && <ToastMsg message={toast.message} type={toast.type} />}
      {planLimitMessage && (
        <PlanLimitModal
          message={planLimitMessage}
          onClose={() => setPlanLimitMessage(null)}
          onViewPlan={() => { setPlanLimitMessage(null); onNavigateUsage?.(); }}
        />
      )}
      {qrItem && (
        <QRModal code={qrItem.code} label={qrItem.label} onClose={() => setQrItem(null)} />
      )}
    </div>
  );
});

const IsleDetail = memo(function IsleDetail({ isle, warehouseId, qrEnabled, onToggleStatus, onShowQR }: { isle: Isle; warehouseId: string; qrEnabled: boolean; onToggleStatus: () => void; onShowQR: (code: string, label: string) => void }) {
  const { t } = useI18n();
  const { canEditWarehouse } = useRole();
  const isActive = isle.status === "ACTIVE";
  const inactive = !isActive;

  return (
    <div style={{ background: inactive ? "#f5f5f5" : "#fff", border: `1.5px solid ${inactive ? "#e0e0e0" : "#e8e8e8"}`, borderRadius: "14px", padding: "20px", display: "flex", flexDirection: "column", gap: "16px", opacity: inactive ? 0.7 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111" }}>{isle.name}</div>
          <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa", marginTop: "2px" }}>
            {isle.isOrganized
              ? `${t.isle.rackType} · ${isle.levels} × ${isle.columns}`
              : `${t.isle.floorIslandType} · ${isle.capacity ?? 0} ${t.isle.capacityUsed}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          {qrEnabled && <button
            onClick={() => onShowQR(isle.name, isle.name)}
            style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: "#666", background: "transparent", border: "1px solid #e8e8e8", borderRadius: "7px", padding: "6px 12px", cursor: "pointer", transition: `all ${transitions.fast}` }}
          >
            QR
          </button>}
          {canEditWarehouse && <button
            onClick={onToggleStatus}
            style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: isActive ? colors.error : "#16a34a", background: "transparent", border: `1px solid ${isActive ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)"}`, borderRadius: "7px", padding: "6px 12px", cursor: "pointer", transition: `all ${transitions.fast}` }}
          >
            {isActive ? t.isle.deactivate : t.isle.activate}
          </button>}
        </div>
      </div>

      {isle.isOrganized && <RackGrid isleId={isle.id} warehouseId={warehouseId} levels={isle.levels ?? 0} columns={isle.columns ?? 0} disabled={inactive} />}

      {!isle.isOrganized && <FloorIslandBlock isle={isle} inactive={inactive} />}
    </div>
  );
});

const FloorIslandBlock = memo(function FloorIslandBlock({ isle, inactive }: { isle: Isle; inactive: boolean }) {
  const { t } = useI18n();
  const [units, setUnits] = useState<import("@/domain/types").TypeUnit[]>([]);
  const [usedCapacity, setUsedCapacity] = useState(0);
  const [loadingStock, setLoadingStock] = useState(true);
  const capacity = isle.capacity ?? 0;
  const usedPct = capacity > 0 ? Math.round((usedCapacity / capacity) * 100) : 0;

  useEffect(() => { listTypeUnits().then(setUnits).catch(() => {}); }, []);

  useEffect(() => {
    setLoadingStock(true);
    listLocationsByIsle(isle.id).then(locs => {
      if (locs.length === 0) { setLoadingStock(false); return; }
      const total = locs.reduce((sum, loc) => sum + (loc.currentStock ?? 0), 0);
      setUsedCapacity(total);
      setLoadingStock(false);
    }).catch(() => setLoadingStock(false));
  }, [isle.id]);

  const unit = units.find(u => u.id === isle.typeUnitId);
  const unitLabel = unit?.abbreviation ?? "";

  return (
    <div style={{ background: inactive ? "#f0f0f0" : "#f8fbff", border: `1.5px solid ${inactive ? "#e0e0e0" : "rgba(59,130,246,0.12)"}`, borderRadius: "14px", padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: inactive ? "#aaa" : "#3b82f6" }}>
          {t.isle.floorIslandType} · {capacity.toLocaleString()} {unitLabel} {t.isle.capacityUsed}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ width: "100%", height: "12px", background: inactive ? "#e0e0e0" : "#e8f0fe", borderRadius: "9999px", overflow: "hidden" }}>
          <div style={{ width: `${usedPct}%`, height: "100%", background: inactive ? "#bbb" : "#3b82f6", borderRadius: "9999px", transition: "width 0.4s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: inactive ? "#bbb" : "#666" }}>
            {usedCapacity.toLocaleString()} / {capacity.toLocaleString()} {unitLabel}
          </span>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.bold, color: inactive ? "#bbb" : "#3b82f6" }}>
            {usedPct}%
          </span>
        </div>
      </div>

      {usedCapacity === 0 && !loadingStock && (
        <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb", background: "#f5f5f5", borderRadius: "8px", padding: "10px 14px" }}>
          {t.isle.emptyTitle}
        </div>
      )}
    </div>
  );
});

const RackGrid = memo(function RackGrid({ isleId, warehouseId, levels, columns, disabled }: { isleId: string; warehouseId: string; levels: number; columns: number; disabled: boolean }) {
  const { t } = useI18n();
  const [locations, setLocations] = useState<import("@/domain/types").Location[]>([]);
  const [stockSummary, setStockSummary] = useState<import("@/domain/types").InventoryStockSummary[]>([]);
  const [loadingLocs, setLoadingLocs] = useState(true);
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchLocations = useCallback(async () => {
    setLoadingLocs(true);
    try {
      const { getStockSummary } = await import("@/infrastructure/inventory/inventoryStockService");
      const [locs, summary] = await Promise.all([
        listLocationsByIsle(isleId),
        getStockSummary(warehouseId),
      ]);
      setLocations(locs);
      setStockSummary(summary);
    } catch {}
    setLoadingLocs(false);
  }, [isleId, warehouseId]);

  useEffect(() => { fetchLocations(); }, [fetchLocations, refreshKey]);

  const handleStatusChanged = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  if (loadingLocs && locations.length === 0) return <div style={{ padding: "20px", textAlign: "center" }}><span style={{ width: "16px", height: "16px", border: `2px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;

  if (!loadingLocs && locations.length === 0) return <div style={{ padding: "16px", fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb", textAlign: "center" }}>No locations</div>;

  const grouped: Record<string, import("@/domain/types").Location[]> = {};
  for (const loc of locations) {
    const key = `${loc.level}-${loc.position}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key]!.push(loc);
  }
  for (const key of Object.keys(grouped)) {
    grouped[key]!.sort((a, b) => a.locationCode.localeCompare(b.locationCode));
  }

  const grid: { key: string; locs: import("@/domain/types").Location[] }[][] = [];
  for (let lvl = 1; lvl <= levels; lvl++) {
    const row: { key: string; locs: import("@/domain/types").Location[] }[] = [];
    for (let pos = 1; pos <= columns; pos++) {
      const key = `${lvl}-${pos}`;
      row.push({ key, locs: grouped[key] ?? [] });
    }
    grid.push(row);
  }

  const selectedLocs = selectedCell ? (grouped[selectedCell] ?? []) : [];

  return (
    <>
      <div style={{ overflowX: "auto", paddingBottom: "4px" }}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 44px)`, gap: "5px", width: "fit-content" }}>
          {grid.flat().map(cell => {
            if (cell.locs.length === 0) return <div key={cell.key} style={{ width: "44px", height: "44px", borderRadius: "8px", background: "#f9f9f9", border: "1.5px dashed #e0e0e0" }} />;
            const front = cell.locs[0]!;
            const allInactive = cell.locs.every(l => l.status === "INACTIVE");
            const someInactive = !allInactive && cell.locs.some(l => l.status === "INACTIVE");
            const hasDepth = cell.locs.length > 1;

            const stockInfo = stockSummary.find(s => cell.locs.some(l => l.id === s.locationId));
            const hasStock = stockInfo?.hasStock ?? false;
            const isFull = stockInfo?.isFull ?? false;

            let cellBg: string;
            let cellBorder: string;
            let cellColor: string;

            if (allInactive) {
              cellBg = "#3a3a3a"; cellBorder = "#555"; cellColor = "#888";
            } else if (someInactive) {
              cellBg = "#fff3e0"; cellBorder = "#ffcc80"; cellColor = "#e65100";
            } else if (isFull) {
              cellBg = "#fbe9e7"; cellBorder = "#ef9a9a"; cellColor = "#c62828";
            } else if (hasStock) {
              cellBg = "#fff8e1"; cellBorder = "#ffcc80"; cellColor = "#e65100";
            } else {
              cellBg = "#e8f5e9"; cellBorder = "#c8e6c9"; cellColor = "#2e7d32";
            }
            return (
              <button
                key={cell.key}
                onClick={() => !disabled && setSelectedCell(cell.key)}
                disabled={disabled}
                title={front.locationCode}
                style={{
                  width: "44px", height: "44px", borderRadius: "8px", position: "relative",
                  background: cellBg,
                  border: `1.5px solid ${cellBorder}`,
                  boxShadow: hasDepth ? "3px 3px 0 0 rgba(0,0,0,0.08), 6px 6px 0 0 rgba(0,0,0,0.04)" : "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: typography.fontFamily, fontSize: "8px", fontWeight: typography.weight.bold,
                  color: cellColor,
                  cursor: disabled ? "default" : "pointer", padding: 0,
                  textDecoration: allInactive ? "line-through" : "none",
                  transition: `all ${transitions.fast}`,
                }}
                onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.1)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
              >
                {front.level}-{front.position}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: "14px", marginTop: "12px", flexWrap: "wrap" }}>
          <Legend bg="#e8f5e9" border="#c8e6c9" label={t.location.activeLabel} />
          <Legend bg="#fff8e1" border="#ffcc80" label={t.location.partialLabel} />
          <Legend bg="#fbe9e7" border="#ef9a9a" label="Full" />
          <Legend bg="#3a3a3a" border="#555" label={t.location.inactiveLabel} />
        </div>
      </div>

      {selectedCell && selectedLocs.length > 0 && (
        <LocationDrawer locations={selectedLocs} onClose={() => setSelectedCell(null)} onStatusChanged={handleStatusChanged} />
      )}
    </>
  );
});;

function Legend({ bg, border, label }: { bg: string; border: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: bg, border: `1.5px solid ${border}` }} aria-hidden="true" />
      <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#888" }}>{label}</span>
    </div>
  );
}

function CreateIsleModal({ warehouseId, onSave, onClose }: { warehouseId: string; onSave: (input: Parameters<typeof createIsle>[0]) => void; onClose: () => void }) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [isOrganized, setIsOrganized] = useState(true);
  const [levels, setLevels] = useState("");
  const [columns, setColumns] = useState("");
  const [depth, setDepth] = useState("");
  const [capacity, setCapacity] = useState("");
  const [typeUnitId, setTypeUnitId] = useState("");
  const [units, setUnits] = useState<TypeUnit[]>([]);

  useEffect(() => { listTypeUnits().then(all => setUnits(all.filter(u => u.status === "ACTIVE"))).catch(() => {}); }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const isValid = name.trim().length >= 3 && !!capacity && Number(capacity) > 0 && !!typeUnitId && (
    isOrganized
      ? (!!levels && Number(levels) >= 1 && !!columns && Number(columns) >= 1)
      : true
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    const input: Parameters<typeof createIsle>[0] = {
      warehouseId, name: name.trim(), isOrganized,
      ...(capacity ? { capacity: Number(capacity) } : {}),
      ...(typeUnitId ? { typeUnitId } : {}),
      ...(isOrganized ? { levels: Number(levels), columns: Number(columns), ...(depth ? { depth: Number(depth) } : {}) } : {}),
    };
    onSave(input);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "440px", display: "flex", flexDirection: "column", gap: "18px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.isle.addSection}</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Field label={t.isle.nameLabel} value={name} onChange={setName} placeholder={t.isle.namePlaceholder} required />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.isle.typeLabel} <span style={{ color: colors.primary }}>*</span></span>
            <div style={{ display: "flex", gap: "12px" }}>
              <RadioBtn label={t.isle.rackType} checked={isOrganized} onChange={() => setIsOrganized(true)} />
              <RadioBtn label={t.isle.floorIslandType} checked={!isOrganized} onChange={() => setIsOrganized(false)} />
            </div>
          </div>
          {isOrganized && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
              <Field label={t.isle.levelsLabel} value={levels} onChange={setLevels} placeholder="1" type="number" required />
              <Field label={t.isle.columnsLabel} value={columns} onChange={setColumns} placeholder="1" type="number" required />
              <Field label={t.isle.depthLabel} value={depth} onChange={setDepth} placeholder="1" type="number" />
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <Field label={t.isle.capacityLabel} value={capacity} onChange={setCapacity} placeholder="0" type="number" required />
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.isle.unitLabel} <span style={{ color: colors.primary }}>*</span></span>
              <select value={typeUnitId} onChange={e => setTypeUnitId(e.target.value)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: typeUnitId ? "#111" : "#aaa", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }}>
                <option value="">{t.isle.unitPlaceholder}</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.isle.cancel}</button>
            <button type="submit" disabled={!isValid} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: isValid ? "#111" : "#ccc", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: isValid ? "pointer" : "not-allowed", transition: `background ${transitions.fast}` }} onMouseEnter={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }} onMouseLeave={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}>{t.isle.save}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required = false }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; type?: string; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
        {label} {required && <span style={{ color: colors.primary }}>*</span>}
      </label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} min={type === "number" ? "0" : undefined} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box", transition: `border-color ${transitions.fast}` }} onFocus={e => (e.currentTarget.style.borderColor = "#111")} onBlur={e => (e.currentTarget.style.borderColor = "#e5e5e5")} />
    </div>
  );
}

function RadioBtn({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: checked ? "#111" : "#888" }}>
      <span style={{ width: "18px", height: "18px", borderRadius: "50%", border: `2px solid ${checked ? colors.primary : "#ddd"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {checked && <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: colors.primary }} />}
      </span>
      <input type="radio" checked={checked} onChange={onChange} style={{ display: "none" }} />
      {label}
    </label>
  );
}

function Spinner() {
  return <div style={{ padding: "40px", textAlign: "center" }}><span style={{ width: "20px", height: "20px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}

function ToastMsg({ message, type }: { message: string; type: "success" | "error" }) {
  return <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300, animation: "fadeInUp 0.3s ease" }}>{message}</div>;
}
